// ClimatologyEvaporationForm.jsx
import { useState, useEffect } from "react"
import { UniversalDropDown } from "@fileops/swmm-react"
import { Grid, Button } from '@mui/material'
import "../../css/DemoCode.css"

/**
 * Frame for Climatology Evamporation inputs
 * 
 * @param {JSON} model A SWMM model in JSON format. In most cases this
 * is a 'submodel' containing only a copy of the affected sections. Using a copy allows
 * for continuous react-style editing without forcing the model to run on every keystroke.
 * @param {function} setModel Update function for updating 'model'.
 * @param {JSON} fullModel The 'full' SWMM model that contains supporting data for this component.
 * @returns UI for interactive swmm modeling.
 */
export default function ClimatologyEvaporationForm({model, setModel, fullModel}) {
  const [recovery, setRecovery] = useState((model && model.EVAPORATION)?
  model.EVAPORATION.Recovery?model.EVAPORATION.Recovery:'':'')
  const [dryOnly, setDryOnly] = useState((model && model.EVAPORATION)?
  model.EVAPORATION.DRY_ONLY?model.EVAPORATION.DRY_ONLY:'NO':'NO')
  const [objsIndexes]   = useState([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11])
  const [months]        = useState(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'])
  const [evapSources]   = useState(['Constant Value', 'Time Series', 'Climate File', 'Monthly Averages', 'Temperatures'])
  const [timeSeriesSet] = useState([...(Object.keys(fullModel.TIMESERIES).length===0?[]:Object.keys(fullModel.TIMESERIES))])
  const [patternSet]    = useState([...(
    (
      !fullModel.PATTERNS ||
      Object.keys(fullModel.PATTERNS).length===0 ||
      Object.keys(fullModel.PATTERNS).filter(o=>fullModel.PATTERNS[o].Type==='MONTHLY') === 0
    )?[]:Object.keys(fullModel.PATTERNS).filter(o=>fullModel.PATTERNS[o].Type==='MONTHLY'))])
  const [source, setSource] = useState(
    (model && model.EVAPORATION)?
    (model.EVAPORATION.CONSTANT || model.EVAPORATION.CONSTANT === 0)?'Constant Value':
    model.EVAPORATION.TimeSeries?'Time Series':
    model.EVAPORATION.FILE?'Climate File':
    model.EVAPORATION.MONTHLY?'Monthly Averages':
    model.EVAPORATION.TEMPERATURE?'Temperatures':
    '':
    ''
  )

  useEffect(()=> {
    let theSource = (model && model.EVAPORATION)?
    (model.EVAPORATION.CONSTANT || model.EVAPORATION.CONSTANT === 0)?'Constant Value':
    (model.EVAPORATION.TimeSeries || model.EVAPORATION.TimeSeries === '')?'Time Series':
    (model.EVAPORATION.FILE)?'Climate File':
    model.EVAPORATION.MONTHLY?'Monthly Averages':
    model.EVAPORATION.TEMPERATURE===''?'Temperatures':
    '':
    ''
    setDryOnly((model && model.EVAPORATION)?
    model.EVAPORATION.DRY_ONLY?model.EVAPORATION.DRY_ONLY:'NO':'NO')
    setSource(theSource)
  }, [model.EVAPORATION])

  const updateSource = (newSource) => {
    switch (newSource) {
      case 'Constant Value': 
        if(!model.EVAPORATION.CONSTANT){
          setModel(prev => ({
            ...prev,
            EVAPORATION: {
              CONSTANT: 0,
              Recovery: recovery,
              DRY_ONLY: dryOnly
            }
          }))
        }
        break
      case 'Time Series': 
        if(!model.EVAPORATION.TimeSeries){
          setModel(prev => ({
            ...prev,
            EVAPORATION: {
              TimeSeries: '',
              Recovery: recovery,
              DRY_ONLY: dryOnly
            }
          }))
        }
        break
      case 'Climate File': 
        if(!model.EVAPORATION.FILE){
          setModel(prev => ({
            ...prev,
            EVAPORATION: {
              FILE: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
              Recovery: recovery,
              DRY_ONLY: dryOnly
            }
          }))
        }
        break
      case 'Monthly Averages': 
        if(!model.EVAPORATION.MONTHLY){
          setModel(prev => ({
            ...prev,
            EVAPORATION: {
              MONTHLY: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
              Recovery: recovery,
              DRY_ONLY: dryOnly
            }
          }))
        }
        break
      case 'Temperatures': 
        if(!model.EVAPORATION.TEMPERATURE){
          setModel(prev => ({
            ...prev,
            EVAPORATION: {
              TEMPERATURE: '',
              Recovery: recovery,
              DRY_ONLY: dryOnly
            }
          }))
        }
        break
      default:
        break
    }
    setSource(newSource)
  }

  // When there is a change to an input element, modify the model.
  const updateConstantEvap = (val) => {
    // Don't let any undefined updates go through.
    if(val === undefined) val = 0
    if(model && model.EVAPORATION){
      setModel(prev => ({
        ...prev,
        EVAPORATION: {
          CONSTANT: val,
          Recovery: recovery,
          DRY_ONLY: dryOnly
        }
      }))
    }
  }

  // When there is a change to an input element, modify the model.
  const updateFilePanCoeff = (val, index) => {
    let FILE = model.EVAPORATION.FILE 
    FILE[index] = val
    // Don't let any undefined updates go through.
    if(val === undefined) val = ""
    if(model && model.EVAPORATION){
      setModel(prev => ({
        ...prev,
        EVAPORATION: {
          FILE: FILE,
          Recovery: recovery,
          DRY_ONLY: dryOnly
        }
      }))
    }
  }

  const updateFileMonthlyEvap = (val, index) => {
    let MONTHLY = model.EVAPORATION.MONTHLY 
    MONTHLY[index] = val
    // Don't let any undefined updates go through.
    if(val === undefined) val = ""
    if(model && model.EVAPORATION){
      setModel(prev => ({
        ...prev,
        EVAPORATION: {
          MONTHLY: MONTHLY,
          Recovery: recovery,
          DRY_ONLY: dryOnly
        }
      }))
    }
  }

  // When there is a change to an input element, modify the model.
  const updateTimeSeries = (val) => {
    // Don't let any undefined updates go through.
    if(val === undefined) return
    if(model && model.EVAPORATION){
      setModel(prev => ({
        ...prev,
        EVAPORATION: {
          TimeSeries: val,
          Recovery: recovery,
          DRY_ONLY: dryOnly
        }
      }))
    }
  }

  // When there is a change to an input element, modify the model.
  const updateRecovery = (val) => {
    // Don't let any undefined updates go through.
    if(val === undefined) return
    setRecovery(val)
    if(val === ''){
      delete model.EVAPORATION.Recovery
    } else {
      let EVAPORATION = model.EVAPORATION
      if(model && model.EVAPORATION){
        setModel(prev => ({
          ...prev,
          EVAPORATION: {
            ...EVAPORATION,
            Recovery: val
          }
        }))
      }
    }
  }

  // When there is a change to an input element, modify the model.
  const updateDryOnly = (val) => {
    // Don't let any undefined updates go through.
    if(val === undefined) return
    if(val === true){
      val = 'YES'
    } else {
      val = 'NO'
    }
    setDryOnly(val)
    let EVAPORATION = model.EVAPORATION
    if(model && model.EVAPORATION){
      setModel(prev => ({
        ...prev,
        EVAPORATION: {
          ...EVAPORATION,
          DRY_ONLY: val
        }
      }))
    }
  }

  return(
    <>
    {
      model.EVAPORATION &&
      source &&
      <>
      <label className='editor-small' >Source of Evaporation Rates
        <UniversalDropDown style={{padding: '0'}}
          IDs={evapSources} 
          selectText={"Select a source"} 
          value={source}
          onChange={(val)=>{updateSource(val)}} >
        </UniversalDropDown>
      </label>
      </>
    }
    {
      model.EVAPORATION &&
      source === 'Constant Value' &&
      <>
      <label className='editor-small' >Daily Evaporation
        <input  type = "text"
                style={{width: '95%', display: 'flex'}}
                onChange={(e)=>{updateConstantEvap(e.target.value)}}
                value={model.EVAPORATION.CONSTANT?model.EVAPORATION.CONSTANT:0}
                id={'ConstantEvap'}
        />
      </label>
      </>
    }
    {
      model.EVAPORATION &&
      source === 'Time Series' &&
      <>
      <label className='editor-small' >Name of Time Series
      <Button style={{padding: '0px', float:'right', display:'inline' }} onClick={()=>{updateTimeSeries('')}}>CLEAR</Button>
        <UniversalDropDown style={{padding: '0'}}
          IDs={timeSeriesSet} 
          selectText={"Select a Time Series"} 
          value={model.EVAPORATION.TimeSeries?model.EVAPORATION.TimeSeries:''}
          onChange={(val)=>{updateTimeSeries(val)}} >
        </UniversalDropDown>
      </label>
      </>
    }
    {
      model.EVAPORATION &&
      source === 'Climate File' &&
      model.EVAPORATION.FILE &&
      <>
      <label className='editor-small' style={{paddingTop: '15px'}}>Pan Coefficients:</label>
      <Grid container>
        {
          objsIndexes.slice(0, 6).map(v=>
            <Grid item xs={2} padding={1} key={'ClimatologyPanCoefficient'+v.toString()}>
              <label className='editor-small' >{months[v]}
                <input  type = "number"
                        style={{width: '95%', display: 'flex'}}
                        onChange={(e)=>{updateFilePanCoeff(e.target.value, v)}}
                        value={model.EVAPORATION.FILE[v]}
                        id={'ClimatologyPanCoefficient'+v.toString()}
                />
              </label>
            </Grid>
          )
        }
      </Grid>
      <Grid container>
        {
          objsIndexes.slice(6, 12).map(v=>
            <Grid item xs={2} padding={1} key={'ClimatologyPanCoefficient'+v.toString()}>
              <label className='editor-small' >{months[v]}
                <input  type = "number"
                        style={{width: '95%', display: 'flex'}}
                        onChange={(e)=>{updateFilePanCoeff(e.target.value, v)}}
                        value={model.EVAPORATION.FILE[v]}
                        id={'ClimatologyPanCoefficient'+v.toString()}
                />
              </label>
            </Grid>
          )
        }
      </Grid>
      </>
    }
    {
      model.EVAPORATION &&
      source === 'Monthly Averages' &&
      model.EVAPORATION.MONTHLY &&
      <>
      <label className='editor-small' style={{paddingTop: '15px'}}>Monthly Evaporation (in/day):</label>
      <Grid container>
        {
          objsIndexes.slice(0, 6).map(v=>
            <Grid item xs={2} padding={1} key={'ClimatologyMonthlyEvap'+v.toString()}>
              <label className='editor-small' >{months[v]}
                <input  type = "number"
                        style={{width: '95%', display: 'flex'}}
                        onChange={(e)=>{updateFileMonthlyEvap(e.target.value, v)}}
                        value={model.EVAPORATION.MONTHLY[v]}
                        id={'ClimatologyMonthlyEvap'+v.toString()}
                />
              </label>
            </Grid>
          )
        }
      </Grid>
      <Grid container>
        {
          objsIndexes.slice(6, 12).map(v=>
            <Grid item xs={2} padding={1} key={'ClimatologyMonthlyEvap'+v.toString()}>
              <label className='editor-small' >{months[v]}
                <input  type = "number"
                        style={{width: '95%', display: 'flex'}}
                        onChange={(e)=>{updateFileMonthlyEvap(e.target.value, v)}}
                        value={model.EVAPORATION.MONTHLY[v]}
                        id={'ClimatologyMonthlyEvap'+v.toString()}
                />
              </label>
            </Grid>
          )
        }
      </Grid>
      </>
    }
    {
      model.EVAPORATION &&
      source === 'Temperatures' &&
      <>
      <label className='editor-small' style={{paddingTop: '15px'}} >Evaporation rates will be computed using temperatures from the Climate File selected on the Temperature page.
      </label>
      </>
    }
    {
      model.EVAPORATION &&
      <>
      <label className='editor-small' >Monthly Soil Recovery Pattern (Optional)
      <Button style={{padding: '0px', float:'right', display:'inline' }} onClick={()=>{updateRecovery('')}}>CLEAR</Button>
        <UniversalDropDown style={{padding: '0'}}
          IDs={patternSet} 
          selectText={"Select a Pattern"} 
          value={model.EVAPORATION.Recovery?model.EVAPORATION.Recovery:''}
          onChange={(val)=>{updateRecovery(val)}} >
        </UniversalDropDown>
      </label>
      </>
    }
    {
      model.EVAPORATION &&
      <label className='editor-small' >
      <input  type = "checkbox"
              style={{display: 'inline'}}
              onChange={(e)=>{updateDryOnly(e.target.checked)}}
              checked={dryOnly==='YES'}
              id={'ClimatologyDryOnly'}
      />Evaporate Only During Dry Periods
    </label>
    }

    </>
  )
}